import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import LocalCafeRoundedIcon from '@mui/icons-material/LocalCafeRounded';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SearchIcon from '@mui/icons-material/Search';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getClientMenus, getMyDrinks } from '../../services/Clint/UserOrdersApi';
import { getImageUrl } from '../../services/indexMediaBaseUrl';

const isVisibleItem = (item) => Number(item?.status) !== 0;

const isPublicApprovedDrink = (drink) =>
  Number(drink?.is_approved) === 1 &&
  Number(drink?.is_public) === 1 &&
  Number(drink?.status) === 1;

const getItemName = (item, isProduct) => (isProduct ? item?.name : item?.drink_name || item?.name) || 'بدون اسم';

const getFieldsText = (fields) => fields.map((field) => String(field || '').toLowerCase()).join(' ');

const formatPrice = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value || '-';
  return numericValue.toFixed(2);
};

const parseIngredients = (drink) => {
  if (Array.isArray(drink?.ingredients)) return drink.ingredients;

  if (typeof drink?.ingredients === 'string' && drink.ingredients.trim()) {
    try {
      const parsed = JSON.parse(drink.ingredients);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const MenuItemCard = ({ item, isProduct = true, onShowDetails }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const name = getItemName(item, isProduct);
  const imageUrl = getImageUrl(item?.image);
  const drinkRatings = !isProduct && Array.isArray(item?.ratings) ? item.ratings : [];
  const ratingsCount = drinkRatings.length;
  const averageRate =
    ratingsCount > 0
      ? (drinkRatings.reduce((sum, rating) => sum + Number(rating?.rate || 0), 0) / ratingsCount).toFixed(1)
      : null;

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        overflow: 'hidden',
        borderRadius: 2,
        borderColor: alpha(theme.palette.divider, 0.9),
        bgcolor: 'background.paper',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          transform: { xs: 'none', md: 'translateY(-4px)' },
          boxShadow: { xs: 'none', md: `0 14px 30px ${alpha(theme.palette.common.black, 0.1)}` },
          borderColor: alpha(theme.palette.primary.main, 0.45),
        },
      }}
    >
      <Box sx={{ display: { xs: 'flex', sm: 'block' }, height: '100%' }}>
        <Box
          sx={{
            width: { xs: 108, sm: '100%' },
            height: { xs: 'auto', sm: 160, md: 176 },
            minHeight: { xs: 138, sm: 160 },
            flexShrink: 0,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
          }}
        >
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt={name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : isProduct ? (
            <LocalDiningIcon sx={{ fontSize: 46, color: 'text.disabled' }} />
          ) : (
            <LocalCafeRoundedIcon sx={{ fontSize: 46, color: 'text.disabled' }} />
          )}
        </Box>

        <CardContent
          sx={{
            flex: 1,
            minWidth: 0,
            p: { xs: 1.25, sm: 1.75 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 900,
                lineHeight: 1.35,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                minWidth: 0,
              }}
            >
              {name}
            </Typography>

            <Chip
              size="small"
              color="success"
              icon={<PaymentsRoundedIcon />}
              label={`${formatPrice(item?.price)} ج`}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
          </Stack>

          {!isProduct && item?.temperature ? (
            <Chip
              size="small"
              variant="outlined"
              color="info"
              label={item.temperature}
              sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
            />
          ) : null}

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              lineHeight: 1.75,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: isMobile ? 2 : 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {item?.description || 'بدون وصف'}
          </Typography>

          {!isProduct ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 'auto' }}>
              {averageRate ? (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  icon={<StarRoundedIcon />}
                  label={`${averageRate} / 5`}
                  sx={{ fontWeight: 700 }}
                />
              ) : (
                <Chip size="small" variant="outlined" label="لا توجد تقييمات" />
              )}
              <Button
                variant="text"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => onShowDetails?.(item)}
                sx={{ minWidth: 0, px: 1, fontWeight: 800 }}
              >
                التفاصيل
              </Button>
            </Stack>
          ) : null}
        </CardContent>
      </Box>
    </Card>
  );
};

const getCategoryImageUrl = (items = []) => {
  const categoryImage = items[0]?.category?.image;
  const itemImage = items.find((item) => item?.image)?.image;
  return getImageUrl(categoryImage || itemImage);
};

const CategoryCard = ({ categoryName, items, onClick }) => {
  const theme = useTheme();
  const imageUrl = getCategoryImageUrl(items);
  const description = items[0]?.category?.description || items[0]?.category?.name_en || '';

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        borderColor: alpha(theme.palette.divider, 0.9),
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          transform: { xs: 'none', md: 'translateY(-4px)' },
          boxShadow: { xs: 'none', md: `0 14px 30px ${alpha(theme.palette.common.black, 0.1)}` },
          borderColor: alpha(theme.palette.primary.main, 0.5),
        },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        <Box
          sx={{
            height: { xs: 120, sm: 150 },
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
          }}
        >
          {imageUrl ? (
            <Box component="img" src={imageUrl} alt={categoryName} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <CategoryRoundedIcon sx={{ fontSize: 52, color: 'text.disabled' }} />
          )}
        </Box>

        <CardContent sx={{ width: '100%', p: { xs: 1.5, sm: 1.75 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }} noWrap>
              {categoryName}
            </Typography>
            <Chip size="small" color="primary" label={`${items.length} عنصر`} sx={{ fontWeight: 800, flexShrink: 0 }} />
          </Stack>

          {description ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
                lineHeight: 1.7,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {description}
            </Typography>
          ) : null}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const EmptyState = ({ icon, title }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 3, md: 4 },
      textAlign: 'center',
      borderRadius: 2,
      borderStyle: 'dashed',
      bgcolor: 'background.paper',
    }}
  >
    <Box sx={{ color: 'text.disabled', mb: 1 }}>{icon}</Box>
    <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 800 }}>
      {title}
    </Typography>
  </Paper>
);

const CategoryHeader = ({ categoryName, count, onBack }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 1.25, sm: 1.5 },
      mb: 2,
      borderRadius: 2,
      position: { xs: 'sticky', md: 'static' },
      top: { xs: 66, md: 'auto' },
      zIndex: 2,
      bgcolor: alpha('#fff', 0.94),
      backdropFilter: 'blur(10px)',
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ borderRadius: 2, fontWeight: 800, flexShrink: 0 }}
      >
        التصنيفات
      </Button>
      <Box sx={{ minWidth: 0, textAlign: 'left' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }} noWrap>
          {categoryName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {count} عنصر
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const Menu = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [products, setProducts] = useState([]);
  const [drinks, setDrinks] = useState([]);
  const [myDrinks, setMyDrinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const [selectedDrink, setSelectedDrink] = useState(null);
  const [selectedProductCategory, setSelectedProductCategory] = useState('');

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    setLoading(true);
    setError('');
    try {
      const [menuResponse, myDrinksResponse] = await Promise.all([
        getClientMenus(),
        getMyDrinks(),
      ]);

      setProducts((menuResponse?.data?.products || []).filter(isVisibleItem));
      setDrinks((menuResponse?.data?.drinks || []).filter(isPublicApprovedDrink));
      setMyDrinks(myDrinksResponse?.data?.order || []);
    } catch (err) {
      console.error('خطأ في جلب المنيو:', err);
      setError(err.message || 'حدث خطأ في جلب المنيو');
    } finally {
      setLoading(false);
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      if (!normalizedSearch) return true;

      return getFieldsText([
        product?.name,
        product?.name_en,
        product?.description,
        product?.description_en,
        product?.code,
        product?.category?.name,
        product?.category?.name_en,
      ]).includes(normalizedSearch);
    });
  }, [products, normalizedSearch]);

  const groupedProducts = useMemo(() => {
    return filteredProducts.reduce((acc, product) => {
      const categoryName = product?.category?.name || product?.category?.name_en || 'بدون فئة';
      if (!acc[categoryName]) acc[categoryName] = [];
      acc[categoryName].push(product);
      return acc;
    }, {});
  }, [filteredProducts]);

  const categoryEntries = Object.entries(groupedProducts);

  const filteredDrinks = useMemo(() => {
    return drinks.filter((drink) => {
      if (!normalizedSearch) return true;

      return getFieldsText([
        drink?.drink_name,
        drink?.name,
        drink?.description,
        drink?.temperature,
        drink?.taste,
        drink?.code,
        drink?.category?.name,
        drink?.category?.name_en,
      ]).includes(normalizedSearch);
    });
  }, [drinks, normalizedSearch]);

  const filteredMyDrinks = useMemo(() => {
    return myDrinks.filter((drink) => {
      if (!normalizedSearch) return true;

      return getFieldsText([
        drink?.drink_name,
        drink?.name,
        drink?.description,
        drink?.temperature,
        drink?.taste,
        drink?.code,
        drink?.approval_notes,
        drink?.category?.name,
        drink?.category?.name_en,
      ]).includes(normalizedSearch);
    });
  }, [myDrinks, normalizedSearch]);

  const activeCount =
    activeTab === 'products'
      ? filteredProducts.length
      : activeTab === 'drinks'
        ? filteredDrinks.length
        : filteredMyDrinks.length;

  const handleTabChange = (event, value) => {
    setActiveTab(value);
    setSelectedProductCategory('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: { xs: 300, md: 420 } }}>
        <CircularProgress size={isMobile ? 44 : 58} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: { xs: 1, md: 2 }, borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box
      dir="rtl"
      sx={{
        px: { xs: 0, sm: 1, md: 2 },
        pb: { xs: 2, md: 4 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2.5 },
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.18),
          background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.09)}, ${alpha(theme.palette.success.main, 0.07)})`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 42, md: 50 },
                height: { xs: 42, md: 50 },
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'primary.main',
                color: 'common.white',
                flexShrink: 0,
              }}
            >
              <MenuBookRoundedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                المنيو
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                تصفح المنتجات والمشروبات المتاحة في Nuvvy
              </Typography>
            </Box>
          </Stack>

          <Chip
            color="primary"
            variant="outlined"
            label={`${activeCount} عنصر ظاهر`}
            sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, fontWeight: 800 }}
          />
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1, sm: 1.25 },
          mb: 2,
          borderRadius: 2,
          position: { xs: 'sticky', md: 'static' },
          top: { xs: 64, md: 'auto' },
          zIndex: 3,
          bgcolor: alpha('#fff', 0.95),
          backdropFilter: 'blur(10px)',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              px: { xs: 1.25, sm: 2 },
              fontWeight: 900,
              fontSize: { xs: 12, sm: 13 },
              textTransform: 'none',
            },
          }}
        >
          <Tab value="products" icon={<LocalDiningIcon fontSize="small" />} iconPosition="start" label={`المنتجات (${products.length})`} />
          <Tab value="drinks" icon={<LocalCafeRoundedIcon fontSize="small" />} iconPosition="start" label={`مشروبات العملاء (${drinks.length})`} />
          <Tab value="my-drinks" icon={<RestaurantMenuIcon fontSize="small" />} iconPosition="start" label={`مشروباتي (${myDrinks.length})`} />
        </Tabs>

        <TextField
          fullWidth
          size="small"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="ابحث بالاسم أو الوصف أو الفئة"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mt: 1.25 }}
        />
      </Paper>

      {activeTab === 'products' && products.length === 0 ? (
        <EmptyState icon={<LocalDiningIcon sx={{ fontSize: 62 }} />} title="لا توجد منتجات" />
      ) : activeTab === 'products' && categoryEntries.length === 0 ? (
        <EmptyState icon={<SearchIcon sx={{ fontSize: 62 }} />} title="لا توجد نتائج مطابقة للبحث" />
      ) : activeTab === 'products' && !selectedProductCategory ? (
        <Grid container spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
          {categoryEntries.map(([categoryName, categoryProducts]) => (
            <Grid item xs={6} sm={6} md={4} lg={3} key={categoryName}>
              <CategoryCard
                categoryName={categoryName}
                items={categoryProducts}
                onClick={() => setSelectedProductCategory(categoryName)}
              />
            </Grid>
          ))}
        </Grid>
      ) : activeTab === 'products' ? (
        <Box>
          <CategoryHeader
            categoryName={selectedProductCategory}
            count={(groupedProducts[selectedProductCategory] || []).length}
            onBack={() => setSelectedProductCategory('')}
          />
          <Grid container spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
            {(groupedProducts[selectedProductCategory] || []).map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <MenuItemCard item={product} isProduct />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : null}

      {activeTab === 'drinks' && drinks.length === 0 ? (
        <EmptyState icon={<LocalCafeRoundedIcon sx={{ fontSize: 62 }} />} title="لا توجد مشروبات عملاء متاحة" />
      ) : activeTab === 'drinks' && filteredDrinks.length === 0 ? (
        <EmptyState icon={<SearchIcon sx={{ fontSize: 62 }} />} title="لا توجد نتائج مطابقة للبحث" />
      ) : activeTab === 'drinks' ? (
        <Grid container spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
          {filteredDrinks.map((drink) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={drink.id}>
              <MenuItemCard item={drink} isProduct={false} onShowDetails={setSelectedDrink} />
            </Grid>
          ))}
        </Grid>
      ) : null}

      {activeTab === 'my-drinks' && myDrinks.length === 0 ? (
        <EmptyState icon={<RestaurantMenuIcon sx={{ fontSize: 62 }} />} title="لا توجد مشروبات خاصة بك" />
      ) : activeTab === 'my-drinks' && filteredMyDrinks.length === 0 ? (
        <EmptyState icon={<SearchIcon sx={{ fontSize: 62 }} />} title="لا توجد نتائج مطابقة للبحث" />
      ) : activeTab === 'my-drinks' ? (
        <Grid container spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
          {filteredMyDrinks.map((drink, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`${drink?.id || 'drink'}-${index}`}>
              <MenuItemCard item={drink} isProduct={false} onShowDetails={setSelectedDrink} />
            </Grid>
          ))}
        </Grid>
      ) : null}

      <Dialog
        open={!!selectedDrink}
        onClose={() => setSelectedDrink(null)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        dir="rtl"
      >
        <DialogTitle sx={{ fontWeight: 900, pb: 1.25 }}>
          تفاصيل المشروب: {selectedDrink?.drink_name || selectedDrink?.name || '-'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          {selectedDrink ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`الكود: ${selectedDrink.code || '-'}`} />
                <Chip label={`السعر: ${formatPrice(selectedDrink.price)} ج`} color="success" />
                <Chip label={`الطعم: ${selectedDrink.taste || '-'}`} color="info" />
                <Chip label={`الحرارة: ${selectedDrink.temperature || '-'}`} color="error" />
                <Chip label={`الحالة: ${Number(selectedDrink.status) === 1 ? 'متاح' : 'غير متاح'}`} />
                <Chip label={`عام: ${Number(selectedDrink.is_public) === 1 ? 'نعم' : 'لا'}`} color="secondary" />
              </Stack>

              <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                  البيانات الأساسية
                </Typography>
                <Grid container spacing={1.25}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>مشروب:</strong> {selectedDrink.drink_name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>الاسم المخصص:</strong> {selectedDrink.name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2"><strong>وصف:</strong> {selectedDrink.description || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>تاريخ إنشاء:</strong> {formatDate(selectedDrink.created_at)}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                  المواد ({selectedDrink.materials?.length || 0})
                </Typography>
                {!selectedDrink.materials?.length ? (
                  <Typography variant="body2" color="text.secondary">لا توجد مواد مرتبطة</Typography>
                ) : (
                  <Stack spacing={1}>
                    {selectedDrink.materials.map((material, index) => (
                      <Paper key={`${material?.id || 'mat'}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} flexWrap="wrap" useFlexGap>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {material?.material?.name || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {material?.quantity || '-'} {material?.material?.measurment_unit?.abbreviation || material?.material?.measurment_unit?.name || ''}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                  المكونات
                </Typography>
                {parseIngredients(selectedDrink).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">لا توجد مكونات</Typography>
                ) : (
                  <Stack spacing={1}>
                    {parseIngredients(selectedDrink).map((ingredient, index) => (
                      <Paper key={`${ingredient?.material_id || 'ing'}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} flexWrap="wrap" useFlexGap>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {ingredient?.name || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {ingredient?.amount || '-'} {ingredient?.unit || ''}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                  التقييمات ({selectedDrink.ratings?.length || 0})
                </Typography>
                {!selectedDrink.ratings?.length ? (
                  <Typography variant="body2" color="text.secondary">لا توجد تقييمات</Typography>
                ) : (
                  <Stack spacing={1}>
                    {selectedDrink.ratings.map((rating, index) => (
                      <Paper key={`${rating?.id || 'rate'}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            التقييم: {rating?.rate || 0} / 5
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            المستخدم: {rating?.user?.name || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            التعليق: {rating?.comment || 'بدون تعليق'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(rating?.created_at)}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button variant="contained" onClick={() => setSelectedDrink(null)} sx={{ borderRadius: 2, fontWeight: 800 }}>
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Menu;
